import { definedMap, withDefault } from "collection-utils";

import {
    ComponentKind,
    TitleDescription,
    PropertyDescription,
    TableGlideType,
    getPrimitiveColumns,
    InputOutputTables,
    MutatingScreenKind,
    DefaultableComponentDescription,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { ComponentDescriptor, ComponentBuilder, ComponentConfiguratorContext, PropertyDescriptor } from ".";
import { ComponentHandlerBase } from "./handler";
import { getColumnProperty, getSwitchProperty } from "./property-utils";
import {
    makePrimaryKeyPropertyHandler,
    makeCaptionStringPropertyDescriptor,
    makeTextPropertyDescriptor,
    makeDefaultValuePropertyDescriptor,
} from "./descriptor-utils";
import { ColumnPropertyHandler, ColumnPropertyFlag, SwitchPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { Doc, getDocURL } from "../../docUrl";
import { SerializedElementBuilder } from "../serialized-component";
import { setColumnInRowForEditComponent, getDefaultValuesForComponent, makeText } from "../builder-utils";

export interface ToggleComponentDescription extends DefaultableComponentDescription, TitleDescription {
    readonly propertyName: PropertyDescription;
    readonly primaryKeyProperty: PropertyDescription | undefined;
    readonly caption: PropertyDescription | undefined;
    readonly description: PropertyDescription | undefined;
    readonly mustBeActive: PropertyDescription | undefined;
    readonly allowWrapping: PropertyDescription | undefined;
}

const allowWrappingPropertyHandler = new SwitchPropertyHandler(
    { allowWrapping: false },
    "Allow text wrapping",
    PropertySection.TextStyle
);

export abstract class ToggleComponentHandler extends ComponentHandlerBase<ToggleComponentDescription> {
    private readonly _propertyNamePropertyHandler: ColumnPropertyHandler<ToggleComponentDescription>;

    protected abstract readonly name: string;
    protected abstract readonly description: string;
    protected abstract readonly img: string;
    protected abstract readonly docPath: Doc;

    protected abstract readonly uiName: string;

    constructor(kind: ComponentKind, preferredNames: readonly string[]) {
        super(kind);

        this._propertyNamePropertyHandler = new ColumnPropertyHandler(
            "propertyName",
            "Column",
            [
                ColumnPropertyFlag.Required,
                ColumnPropertyFlag.Editable,
                ColumnPropertyFlag.DefaultCaption,
                ColumnPropertyFlag.EditedInApp,
            ],
            undefined,
            preferredNames,
            getPrimitiveColumns,
            "boolean",
            PropertySection.Data
        );
    }

    public needValidation(desc: ToggleComponentDescription): boolean {
        return withDefault(getSwitchProperty(desc.mustBeActive), false);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public get isNonEmptyValidationStopper(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ToggleComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = this._propertyNamePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const booleanValue = b.convertToBoolean(b.getProperty(propertyName, outputContext, tables.output));

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            booleanValue,
            b.constant(false),
            tables.output,
            definedMap(desc.primaryKeyProperty, getColumnProperty),
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const allowWrapping = allowWrappingPropertyHandler.getSwitch(desc);

        return b.let(
            lets,
            b.ui(this.uiName, {
                value: valueToModify,
                onChange: action,
                isEnabled,
                caption: makeText(builder, desc.caption, tables.input, true),
                description: makeText(builder, desc.description, tables.input, true),
                allowWrapping: b.data(allowWrapping),
            })
        );
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: ToggleComponentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        const propertyName = getColumnProperty(desc.propertyName);
        if (propertyName === undefined) return undefined;
        const property = b.getProperty(propertyName, outputContext, table);
        return b.convertToBoolean(property);
    }

    public getDescriptor(
        _desc: ToggleComponentDescription,
        table: TableGlideType | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<ToggleComponentDescription> {
        const properties: PropertyDescriptor[] = [this._propertyNamePropertyHandler];

        const defaultDescr = makeDefaultValuePropertyDescriptor(ccc, mutatingScreenKind, "boolean");
        if (defaultDescr !== undefined) {
            properties.push(defaultDescr);
        }

        properties.push(
            ...makePrimaryKeyPropertyHandler(ccc, table),
            makeCaptionStringPropertyDescriptor("Toggle", false, mutatingScreenKind),
            makeTextPropertyDescriptor("description", "Description", "Enter description", false, mutatingScreenKind, {
                searchable: false,
                propertySection: PropertySection.Design,
            }),
            allowWrappingPropertyHandler,
            ...this.getBasePropertyDescriptors()
        );
        return {
            name: this.name,
            description: this.description,
            img: this.img,
            group: "Buttons",
            helpUrl: getDocURL(this.docPath),
            properties,
        };
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: ToggleComponentDescription,
        { input }: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        const defaultValues = getDefaultValuesForComponent(builder, desc, input, "boolean");
        if (defaultValues.length > 0) {
            return defaultValues;
        }

        const propertyName = this._propertyNamePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return [];

        return [[propertyName, builder.b.data(false)]];
    }
}
